import { useSelector } from 'react-redux';
import { useIsCourseStudent } from '../instances/useInstance';
import { examsSelectors } from '../slices/exams/examsSlice';
import { selectCurrentUser } from '../slices/user/userSlice';
import type { User, UserExam } from '../types/User';
import {
  isComlexExam,
  isNbmeExam,
  isNclexExam,
  isPreclinicalExam,
} from './examUtils';

/** The demo student account shown in webinars (ora-be `demoAccountConfig`). */
export const DEMO_STUDENT_ACCOUNT_EMAIL = 'demo-student@oraai.com';

/**
 * The knowledge map (ORA-867) is open to every signed-in student. It still
 * needs WebGL2 and a desktop-width window, which its callers check.
 */
export const canUseKnowledgeMap = (user: User | null): boolean => user !== null;

/**
 * Whether the map is offered for this student on this exam: Preclinical, a
 * USMLE Step or Shelf exam (the NBME type) or COMLEX, which get the medical
 * map (ORA-1032), or NCLEX, which gets the nursing map of the NCLEX topics
 * (ORA-1086; the backend picks the map from the exam). Hidden on residency
 * (sub-internships), courses (custom exams) and Free Study, and before an
 * exam is picked. A course student (Courses V1) is offered their course's
 * own map instead, whatever the exam.
 */
export const isKnowledgeMapOffered = (
  user: User | null,
  primaryExam?: UserExam | null,
  isCourseStudent = false
): boolean =>
  canUseKnowledgeMap(user) &&
  (isCourseStudent ||
    isPreclinicalExam(primaryExam) ||
    isNbmeExam(primaryExam) ||
    isComlexExam(primaryExam) ||
    isNclexExam(primaryExam));

/** Whether the signed-in user is offered the knowledge map on their exam. */
export const useKnowledgeMapEnabled = (): boolean =>
  isKnowledgeMapOffered(
    useSelector(selectCurrentUser),
    useSelector(examsSelectors.selectPrimaryExam),
    useIsCourseStudent()
  );
